import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import PlayerPlay from '@jwp/ott-theme/assets/icons/player_Play_Arrow.svg?react';
import { formatDurationTag } from '@jwp/ott-common/src/utils/formatting';

import Player from '../Player/Player';
import ShowMoreDetails from '../ShowMoreDetails/ShowMoreDetails';
import ModalCloseButton from '../ModalCloseButton/ModalCloseButton';
import type { JWPlayer } from '../../../types/jwplayer';
import { useTheme } from '../../../../../ThemeContext';
import CustomPlayerHeader from './CustomPlayerHeader';
import CustomPlayerProgress from './CustomPlayerProgress';
import CustomPlayerControls from './CustomPlayerControls';
import { PauseIcon } from './CustomPlayerIcons';
import {
  defaultCustomPlayerFeatures,
  defaultCustomPlayerTheme,
  themeToCssVars,
} from './CustomPlayerTheme';
import type { CustomPlayerProps, QualityOption, SettingsPanelType, SubtitleOption, AudioTrackOption } from './CustomPlayer.types';
import {
  applyAudioTrackToPlayer,
  applyCaptionsToPlayer,
  buildAudioTrackOptions,
  buildSubtitleOptions,
  collectAudioTracksFromPlayer,
  collectCaptionTrackSources,
  createAudioTrackSelections,
  enrichAudioTracksWithManifestUris,
  ensureAudioTrackDuration,
  evaluateAudioVideoDurationGuard,
  findAudioTrackIndex,
  getActiveCaptionText,
  getAudioTrackSelectionId,
  getPlayerVideo,
  getSelectedAudioTrack,
  getSidecarCueText,
  getSwitchableAudioTracks,
  hasAudioTrackOptions,
  hasCaptionTrackOptions,
  loadCaptionSidecars,
  mergeCaptionSelections,
  type AudioTrack,
  type CaptionSource,
  type CaptionTrack,
  type CaptionsCapablePlayer,
  type SidecarCaptionTrack,
} from './customPlayerTracks';
import {
  loadChapters,
  loadTrickPlayTrack,
  preloadTrickPlayImages,
  type PlaylistItemLike,
  type TrickPlayChapter,
  type TrickPlayTrack,
} from './customPlayerTrickPlay';
import skipIntroStyles from './CustomPlayerSkipIntro.module.scss';
import styles from './CustomPlayer.module.scss';
import { isLiveEvent, MediaStatus } from '@jwp/ott-common/src/utils/liveEvent';
import { isLiveChannel } from '@jwp/ott-common/src/utils/media';
import { CONTENT_TYPE } from '@jwp/ott-common/src/constants';

const CONTROLS_HIDE_DELAY = 3000;
const PAUSE_PLAY_HIDE_DELAY = 1000;
const AUTO_QUALITY_VALUE = 'auto';

type PlayerQualityLevel = {
  label?: string;
  bitrate?: number;
  height?: number;
  width?: number;
};

type QualityCapablePlayer = JWPlayer & {
  getQualityLevels?: () => PlayerQualityLevel[];
  getCurrentQuality?: () => number;
  setCurrentQuality?: (index: number) => void;
};

const parseQualityHeight = (label: string) => parseInt(label.replace(/[^\d]/g, ''), 10);

const isAutoQualityLevel = (level: PlayerQualityLevel) => (level.label || '').toLowerCase() === AUTO_QUALITY_VALUE;

const getQualityValue = (level: PlayerQualityLevel, index: number) => {
  if (level.height) {
    return `${level.height}p`;
  }

  const labelHeight = parseQualityHeight(level.label || '');
  if (!Number.isNaN(labelHeight)) {
    return `${labelHeight}p`;
  }

  return level.label || String(index);
};

const formatQualityLabel = (level: PlayerQualityLevel, index: number) => {
  const resolution = level.label || (level.height ? `${level.height}p` : getQualityValue(level, index));
  if (!level.bitrate || /kbps/i.test(resolution)) {
    return resolution;
  }

  const kbps = Math.round(level.bitrate / 1000);
  return kbps > 0 ? `${resolution} (${kbps} kbps)` : resolution;
};

const buildQualityOptions = (levels: PlayerQualityLevel[], autoLabel: string): QualityOption[] => [
  { label: autoLabel, value: AUTO_QUALITY_VALUE },
  ...levels.flatMap((level, index) => {
    if (isAutoQualityLevel(level)) {
      return [];
    }

    return [
      {
        label: formatQualityLabel(level, index),
        value: getQualityValue(level, index),
        bitrate: level.bitrate,
      },
    ];
  }),
];

const findQualityIndex = (levels: PlayerQualityLevel[], value: string) => {
  if (value === AUTO_QUALITY_VALUE) {
    return -1;
  }

  const exactIndex = levels.findIndex((level, index) => {
    if (isAutoQualityLevel(level)) {
      return false;
    }

    return getQualityValue(level, index) === value || level.label === value;
  });

  if (exactIndex >= 0) {
    return exactIndex;
  }

  const preferredHeight = parseQualityHeight(value);
  if (Number.isNaN(preferredHeight)) {
    return -1;
  }

  const rankedLevels = levels
    .map((level, index) => ({
      level,
      index,
      height: level.height || parseQualityHeight(level.label || ''),
    }))
    .filter(({ level, height }) => !isAutoQualityLevel(level) && !Number.isNaN(height))
    .sort((a, b) => b.height - a.height);

  return rankedLevels.find(({ height }) => height <= preferredHeight)?.index ?? rankedLevels[0]?.index ?? -1;
};

const getStoredQuality = () => {
  try {
    const stored = localStorage.getItem('videoQuality');
    if (!stored || stored.toLowerCase() === AUTO_QUALITY_VALUE) {
      return AUTO_QUALITY_VALUE;
    }

    return stored;
  } catch {
    return AUTO_QUALITY_VALUE;
  }
};

const persistQualityPreference = (value: string, levels: PlayerQualityLevel[]) => {
  if (value === AUTO_QUALITY_VALUE) {
    localStorage.setItem('videoQuality', AUTO_QUALITY_VALUE);
    return;
  }

  const index = findQualityIndex(levels, value);
  const level = levels[index];
  localStorage.setItem('videoQuality', level?.label || value);
};

const applyQualityToPlayer = (player: QualityCapablePlayer, value: string) => {
  const levels = player.getQualityLevels?.() ?? [];
  persistQualityPreference(value, levels);
  player.setCurrentQuality?.(findQualityIndex(levels, value));
};

const CustomPlayer: React.FC<CustomPlayerProps> = ({
  config,
  onClose,
  onReady,
  onPlay,
  onPause,
  onUserActive,
  onUserInActive,
  onFirstFrame,
  videoThumnail,
  item,
  ...playerProps
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation('player');
  const { universalStyles } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<JWPlayer>();
  const isScrubbingRef = useRef(false);
  const hideControlsTimer = useRef<ReturnType<typeof setTimeout>>();
  const centerButtonHideTimer = useRef<ReturnType<typeof setTimeout>>();
  const selectedQualityRef = useRef(AUTO_QUALITY_VALUE);
  const selectedSubtitleRef = useRef('off');
  const selectedAudioTrackRef = useRef('');
  const isApplyingAudioTrackRef = useRef(false);
  const captionTextRef = useRef('');
  const captionCleanupRef = useRef<(() => void) | undefined>();
  const sidecarCuesRef = useRef<SidecarCaptionTrack[]>([]);
  const audioTracksRef = useRef<AudioTrack[]>([]);
  const activeAudioDurationRef = useRef<number | undefined>(undefined);
  const audioEndedMuteRef = useRef(false);
  const userMutedRef = useRef(Boolean(playerProps.mute));
  const syncCaptionOverlayRef = useRef<(position?: number) => void>(() => { });

  const theme = useMemo(
    () => ({ ...defaultCustomPlayerTheme, ...config?.theme }),
    [config?.theme],
  );
  const features = useMemo(
    () => ({ ...defaultCustomPlayerFeatures, ...config?.features }),
    [config?.features],
  );
  const callbacks = config?.callbacks ?? {};

  const [qualityOptions, setQualityOptions] = useState<QualityOption[]>([
    { label: t('auto'), value: AUTO_QUALITY_VALUE },
    { label: '1080p (2860 kbps)', value: '1080p' },
    { label: '720p (1400 kbps)', value: '720p' },
    { label: '540p (920 kbps)', value: '540p' },
    { label: '360p (540 kbps)', value: '360p' },
  ]);
  const [subtitleOptions, setSubtitleOptions] = useState<SubtitleOption[]>([{ label: t('off'), value: 'off' }]);
  const [audioTrackOptions, setAudioTrackOptions] = useState<AudioTrackOption[]>([]);
  const playbackSpeedOptions = useMemo(
    () => [
      { label: '0.5x', value: 0.5 },
      { label: '0.75x', value: 0.75 },
      { label: '1x', value: 1 },
      { label: '1.25x', value: 1.25 },
      { label: '1.5x', value: 1.5 },
      { label: '2x', value: 2 },
    ],
    [],
  );

  const [isPlaying, setIsPlaying] = useState(Boolean(playerProps.autostart));
  const [isMuted, setIsMuted] = useState(Boolean(playerProps.mute));
  const [volume, setVolume] = useState(70);
  const [currentTime, setCurrentTime] = useState(playerProps.startTime ?? 0);
  const [duration, setDuration] = useState(item.duration || 0);
  const [showControls, setShowControls] = useState(true);
  const [showCenterButton, setShowCenterButton] = useState(true);
  const [activePanel, setActivePanel] = useState<SettingsPanelType>(null);
  const [selectedQuality, setSelectedQuality] = useState(getStoredQuality);
  const [selectedSubtitle, setSelectedSubtitle] = useState('off');
  const [selectedAudioTrack, setSelectedAudioTrack] = useState('');
  const [selectedPlaybackSpeed, setSelectedPlaybackSpeed] = useState(1);
  const [isLiked, setIsLiked] = useState(true);
  const [likeCount, setLikeCount] = useState(898);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [scrubProgress, setScrubProgress] = useState(0);
  const [showSkipIntro, setShowSkipIntro] = useState(features.skipIntro);
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const [captionText, setCaptionText] = useState('');
  const [isLiveIssueOpen, setIsLiveIssueOpen] = useState(false);
  const [trickPlayTrack, setTrickPlayTrack] = useState<TrickPlayTrack>({ kind: 'none', frames: [] });
  const [chapters, setChapters] = useState<TrickPlayChapter[]>([]);
  selectedQualityRef.current = selectedQuality;
  selectedSubtitleRef.current = selectedSubtitle;
  selectedAudioTrackRef.current = selectedAudioTrack;

  const progress = duration > 0 ? currentTime / duration : 0;
  const posterSrc = videoThumnail || item.image || item.backgroundImage;
  const hasCaptionsAvailable = useMemo(() => hasCaptionTrackOptions(subtitleOptions), [subtitleOptions]);
  const hasMultipleAudioTracks = useMemo(() => hasAudioTrackOptions(audioTrackOptions), [audioTrackOptions]);
  const showAudioTracksControl = features.audioTracks && hasMultipleAudioTracks;
  // Captions button opens Audio Language + Subtitles (Figma). Keep it visible for
  // multi-audio even when no caption tracks exist (Subtitles column shows Off only).
  const showSubtitlesControl = (features.subtitles && hasCaptionsAvailable) || showAudioTracksControl;


  const isLiveContent =
    item.mediaStatus === MediaStatus.LIVE ||
    isLiveEvent(item) ||
    isLiveChannel(item) ||
    item.contentType?.toLowerCase() === CONTENT_TYPE.live ||
    item.contentType?.toLowerCase() === CONTENT_TYPE.liveChannel;

  const syncCaptionOverlay = useCallback((position?: number) => {
    if (selectedSubtitleRef.current === 'off') {
      if (captionTextRef.current) {
        captionTextRef.current = '';
        setCaptionText('');
      }
      return;
    }

    const player = playerRef.current as CaptionsCapablePlayer | undefined;
    const hasExplicitTime = typeof position === 'number' && Number.isFinite(position);
    const time = hasExplicitTime ? position : player?.getPosition?.() ?? 0;
    const nextText =
      getSidecarCueText(sidecarCuesRef.current, selectedSubtitleRef.current, time) ||
      getActiveCaptionText(player, time, containerRef.current, selectedSubtitleRef.current);
    const playerState = player?.getState?.();
    const isPaused = playerState === 'paused' || playerState === 'idle';

    // JW clears native caption nodes on pause and may pass the event object as
    // the first callback argument. Keep the current cue visible until a real
    // seek/time update says it should change.
    if (!nextText && captionTextRef.current && isPaused && !hasExplicitTime) {
      return;
    }

    if (nextText === captionTextRef.current) {
      return;
    }

    captionTextRef.current = nextText;
    setCaptionText(nextText);
  }, []);

  syncCaptionOverlayRef.current = syncCaptionOverlay;

  useEffect(() => {
    if (!isLiveIssueOpen) {
      return;
    }

    setActivePanel(null);
    setShowMoreDetails(false);
  }, [isLiveIssueOpen]);

  useEffect(() => {
    if (!showSubtitlesControl && activePanel === 'subtitles') {
      setActivePanel(null);
    }
  }, [activePanel, showSubtitlesControl]);

  useEffect(() => {
    if (!showAudioTracksControl && activePanel === 'audioTracks') {
      setActivePanel(null);
    }
  }, [activePanel, showAudioTracksControl]);

  const updateAudioTrackState = useCallback((tracks: AudioTrack[], player?: CaptionsCapablePlayer) => {
    audioTracksRef.current = tracks;
    const selections = createAudioTrackSelections(tracks);
    setAudioTrackOptions(buildAudioTrackOptions(selections));

    if (!tracks.length) {
      selectedAudioTrackRef.current = '';
      setSelectedAudioTrack('');
      return;
    }

    const currentIndex = player?.getCurrentAudioTrack?.();
    const currentId =
      typeof currentIndex === 'number' ? getAudioTrackSelectionId(tracks, currentIndex) : selections[0]?.id ?? '';
    const preferredId = selectedAudioTrackRef.current;
    const preferredIndex = preferredId ? findAudioTrackIndex(tracks, preferredId) : -1;
    const nextId = preferredIndex >= 0 ? preferredId : currentId;

    if (nextId) {
      selectedAudioTrackRef.current = nextId;
      setSelectedAudioTrack(nextId);
    }
  }, []);

  const refreshActiveAudioDuration = useCallback(async (selectedId?: string) => {
    const tracks = audioTracksRef.current;
    if (tracks.length <= 1) {
      activeAudioDurationRef.current = undefined;
      audioEndedMuteRef.current = false;
      return;
    }

    const selected = selectedId || selectedAudioTrackRef.current || getAudioTrackSelectionId(tracks, 0);
    const track = getSelectedAudioTrack(tracks, selected);
    const duration = await ensureAudioTrackDuration(track);
    activeAudioDurationRef.current = duration;
  }, []);

  const applyAudioVideoDurationGuard = useCallback(
    (player: CaptionsCapablePlayer, position: number, videoDuration: number) => {
      if (!features.audioTracks || audioTracksRef.current.length <= 1 || videoDuration <= 0) {
        return;
      }

      const result = evaluateAudioVideoDurationGuard({
        hasMultipleAudioTracks: true,
        audioDuration: activeAudioDurationRef.current,
        videoDuration,
        position,
        userMuted: userMutedRef.current,
        audioEndedMute: audioEndedMuteRef.current,
        playerState: player.getState?.(),
      });

      audioEndedMuteRef.current = result.audioEndedMute;

      if (result.pause) {
        player.pause?.();
        getPlayerVideo(player, containerRef.current)?.pause();
      }

      if (result.mute) {
        player.setMute?.(true);
        setIsMuted(true);
      } else if (result.unmute) {
        player.setMute?.(false);
        setIsMuted(false);
      }

      if (result.resumePlay) {
        player.setMute?.(true);
        setIsMuted(true);
        player.play?.();
      }
    },
    [features.audioTracks],
  );

  const discoverAudioTracks = useCallback(
    async (player?: CaptionsCapablePlayer) => {
      const captionsPlayer = player ?? (playerRef.current as CaptionsCapablePlayer | undefined);
      if (!captionsPlayer) {
        return;
      }

      let tracks = getSwitchableAudioTracks(captionsPlayer);
      if (tracks.length <= 1) {
        const collectedTracks = collectAudioTracksFromPlayer(captionsPlayer);
        tracks = collectedTracks.length > 1 ? collectedTracks : [];
      }

      if (tracks.length > 1) {
        const playlist = captionsPlayer.getPlaylistItem?.() as
          | { file?: string; sources?: Array<{ file?: string }> }
          | undefined;
        tracks = await enrichAudioTracksWithManifestUris(
          tracks,
          playlist?.file,
          ...(playlist?.sources?.map((source) => source.file) || []),
          ...(item.sources?.map((source) => source.file) || []),
        );
      }

      updateAudioTrackState(tracks, captionsPlayer);
      await refreshActiveAudioDuration(selectedAudioTrackRef.current);
    },
    [item.sources, refreshActiveAudioDuration, updateAudioTrackState],
  );

  const resetAudioTrackState = useCallback(() => {
    selectedAudioTrackRef.current = '';
    audioTracksRef.current = [];
    activeAudioDurationRef.current = undefined;
    audioEndedMuteRef.current = false;
    setSelectedAudioTrack('');
    setAudioTrackOptions([]);
  }, []);

  const resetHideTimer = useCallback(() => {
    if (hideControlsTimer.current) {
      clearTimeout(hideControlsTimer.current);
    }
    setShowControls(true);
    onUserActive?.();

    if (isPlaying && !showMoreDetails) {
      hideControlsTimer.current = setTimeout(() => {
        setShowControls(false);
        setActivePanel(null);
        onUserInActive?.();
      }, CONTROLS_HIDE_DELAY);
    }
  }, [isPlaying, onUserActive, onUserInActive, showMoreDetails]);

  useEffect(() => {
    resetHideTimer();
    return () => {
      if (hideControlsTimer.current) {
        clearTimeout(hideControlsTimer.current);
      }
    };
  }, [isPlaying, resetHideTimer]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(
    () => () => {
      captionCleanupRef.current?.();
    },
    [],
  );

  const handlePlayerReady = useCallback(
    (player?: JWPlayer) => {
      if (!player) {
        return;
      }

      playerRef.current = player;
      setDuration(player.getDuration() || item.duration || 0);
      setVolume(player.getVolume());
      setIsMuted(player.getMute());
      setIsPlaying(player.getState() === 'playing');
      setCurrentTime(player.getPosition());

      const syncQualityOptions = () => {
        const levels = (player as QualityCapablePlayer).getQualityLevels?.() ?? [];
        if (!levels.length) {
          return;
        }

        setQualityOptions(buildQualityOptions(levels, t('auto')));
        setSelectedQuality((prev) => {
          if (prev === AUTO_QUALITY_VALUE) {
            return AUTO_QUALITY_VALUE;
          }

          const matchedIndex = findQualityIndex(levels, prev);
          if (matchedIndex < 0) {
            return AUTO_QUALITY_VALUE;
          }

          return getQualityValue(levels[matchedIndex], matchedIndex);
        });
        applyQualityToPlayer(player as QualityCapablePlayer, selectedQualityRef.current);
      };

      player.on('levels', syncQualityOptions);
      syncQualityOptions();

      const captionsPlayer = player as CaptionsCapablePlayer;

      const updateCaptionText = (position?: number) => {
        syncCaptionOverlayRef.current(typeof position === 'number' ? position : undefined);
      };

      const attachCaptionCueListeners = () => {
        captionCleanupRef.current?.();

        const video = getPlayerVideo(captionsPlayer, containerRef.current);
        const container = captionsPlayer.getContainer?.();
        const handleCueChange = () => updateCaptionText();

        if (video?.textTracks) {
          Array.from(video.textTracks).forEach((track) => {
            track.addEventListener('cuechange', handleCueChange);
          });
          video.textTracks.addEventListener('addtrack', handleCueChange);
        }

        video?.addEventListener('seeked', handleCueChange);

        const observer = container ? new MutationObserver(handleCueChange) : null;
        observer?.observe(container as Node, { childList: true, subtree: true, characterData: true });

        captionCleanupRef.current = () => {
          observer?.disconnect();
          video?.removeEventListener('seeked', handleCueChange);
          if (video?.textTracks) {
            Array.from(video.textTracks).forEach((track) => {
              track.removeEventListener('cuechange', handleCueChange);
            });
            video.textTracks.removeEventListener('addtrack', handleCueChange);
          }
        };
      };

      const playlistItem = () => player.getPlaylistItem?.() as PlaylistItemLike | undefined;
      const playlistTracks = () => playlistItem()?.tracks as CaptionSource[] | undefined;

      const loadTrickPlay = async () => {
        const playlist = playlistItem();
        const trickPlayContext = {
          itemTracks: item.tracks,
          playlistTracks: playlist?.tracks,
          sources: [...(item.sources || []), ...(playlist?.sources || [])],
          file: playlist?.file,
        };

        if (features.trickPlay) {
          const track = await loadTrickPlayTrack(trickPlayContext);
          setTrickPlayTrack(track);
          preloadTrickPlayImages(track.frames);
        } else {
          setTrickPlayTrack({ kind: 'none', frames: [] });
        }

        if (features.trickPlay || features.chapters) {
          setChapters(await loadChapters(trickPlayContext));
        } else {
          setChapters([]);
        }
      };
      void loadTrickPlay();
      player.on('playlistItem' as never, () => {
        resetAudioTrackState();
        void loadTrickPlay();
      });

      const refreshSubtitleOptions = (jwTracks?: CaptionTrack[]) => {
        const sources = collectCaptionTrackSources(item.tracks, playlistTracks());
        const captions = jwTracks ?? captionsPlayer.getCaptionsList?.() ?? [];
        setSubtitleOptions(buildSubtitleOptions(mergeCaptionSelections(sources, captions), t('off')));
      };

      const refreshAudioTrackOptions = (jwTracks?: AudioTrack[]) => {
        const liveTracks = jwTracks?.length ? jwTracks : getSwitchableAudioTracks(captionsPlayer);
        if (liveTracks.length > 1) {
          updateAudioTrackState(liveTracks, captionsPlayer);
          return;
        }

        discoverAudioTracks(captionsPlayer);
      };

      const syncCaptions = (captions?: CaptionTrack[]) => {
        applyCaptionsToPlayer(captionsPlayer, selectedSubtitleRef.current, captions, containerRef.current);
        attachCaptionCueListeners();
        updateCaptionText();
      };

      const syncAudioTracks = (audioTracks?: AudioTrack[]) => {
        if (audioTracks) {
          refreshAudioTrackOptions(audioTracks);
        }
      };

      player.on('captionsList' as never, (event?: { tracks?: CaptionTrack[] }) => {
        refreshSubtitleOptions(event?.tracks);
        syncCaptions(event?.tracks);
      });
      player.on('captionsChanged' as never, updateCaptionText);
      player.on('seek' as never, updateCaptionText);
      player.on('pause' as never, updateCaptionText);
      player.on('audioTracks' as never, (event?: { tracks?: AudioTrack[] }) => {
        syncAudioTracks(event?.tracks);
      });
      player.on('audioTrackChanged' as never, (event?: { currentTrack?: number; tracks?: AudioTrack[] }) => {
        if (isApplyingAudioTrackRef.current) {
          return;
        }

        const tracks = event?.tracks ?? captionsPlayer.getAudioTracks?.() ?? audioTracksRef.current;
        if (typeof event?.currentTrack === 'number' && tracks.length) {
          const nextId = getAudioTrackSelectionId(tracks, event.currentTrack);
          if (nextId) {
            selectedAudioTrackRef.current = nextId;
            setSelectedAudioTrack(nextId);
          }
        }
      });
      player.on('firstFrame' as never, () => {
        void discoverAudioTracks(captionsPlayer);
      });
      player.on('meta' as never, () => {
        void discoverAudioTracks(captionsPlayer);
      });
      player.on('levels' as never, () => {
        void discoverAudioTracks(captionsPlayer);
      });
      player.on('play' as never, () => syncCaptions());
      refreshSubtitleOptions();
      syncCaptions();
      void discoverAudioTracks(captionsPlayer);
      window.setTimeout(() => syncCaptions(), 500);
      window.setTimeout(() => void discoverAudioTracks(captionsPlayer), 500);
      window.setTimeout(() => syncCaptions(), 1500);
      window.setTimeout(() => void discoverAudioTracks(captionsPlayer), 1500);
      window.setTimeout(() => void discoverAudioTracks(captionsPlayer), 3000);

      const loadSidecarCaptions = async () => {
        const sources = collectCaptionTrackSources(item.tracks, playlistTracks());
        refreshSubtitleOptions();
        sidecarCuesRef.current = await loadCaptionSidecars(sources);
        updateCaptionText();
      };
      void loadSidecarCaptions();

      player.on('time', () => {
        const position = player.getPosition();
        const nextDuration = player.getDuration() || item.duration || 0;
        if (!isScrubbingRef.current) {
          setCurrentTime(position);
          setDuration(nextDuration);
        }
        updateCaptionText(position);
        applyAudioVideoDurationGuard(captionsPlayer, position, nextDuration);
      });
      player.on('buffer' as never, () => {
        applyAudioVideoDurationGuard(
          captionsPlayer,
          player.getPosition?.() ?? 0,
          player.getDuration?.() || item.duration || 0,
        );
      });
      player.on('complete' as never, () => {
        const nextDuration = player.getDuration?.() || item.duration || 0;
        applyAudioVideoDurationGuard(captionsPlayer, nextDuration, nextDuration);
      });

      onReady?.(player);
    },
    [applyAudioVideoDurationGuard, features.chapters, features.trickPlay, discoverAudioTracks, item.duration, item.sources, item.tracks, onReady, resetAudioTrackState, t, updateAudioTrackState],
  );

  useEffect(() => {
    resetAudioTrackState();

    const player = playerRef.current as CaptionsCapablePlayer | undefined;
    if (!player) {
      return;
    }

    void discoverAudioTracks(player);

    const playlistTracks = () =>
      (player.getPlaylistItem?.() as { tracks?: CaptionSource[] } | undefined)?.tracks;
    const sources = collectCaptionTrackSources(item.tracks, playlistTracks());
    const jwTracks = player.getCaptionsList?.() ?? [];
    setSubtitleOptions(buildSubtitleOptions(mergeCaptionSelections(sources, jwTracks), t('off')));

    void loadCaptionSidecars(sources).then((loaded) => {
      sidecarCuesRef.current = loaded;
      applyCaptionsToPlayer(player, selectedSubtitleRef.current, undefined, containerRef.current);

      if (selectedSubtitleRef.current === 'off') {
        return;
      }

      syncCaptionOverlay(player.getPosition?.() ?? 0);
    });

    if (!features.trickPlay && !features.chapters) {
      setTrickPlayTrack({ kind: 'none', frames: [] });
      setChapters([]);
      return;
    }

    const playlist = player.getPlaylistItem?.() as PlaylistItemLike | undefined;
    const trickPlayContext = {
      itemTracks: item.tracks,
      playlistTracks: playlist?.tracks,
      sources: [...(item.sources || []), ...(playlist?.sources || [])],
      file: playlist?.file,
    };

    if (features.trickPlay) {
      void loadTrickPlayTrack(trickPlayContext).then((track) => {
        setTrickPlayTrack(track);
        preloadTrickPlayImages(track.frames);
      });
    } else {
      setTrickPlayTrack({ kind: 'none', frames: [] });
    }

    void loadChapters(trickPlayContext).then(setChapters);
  }, [discoverAudioTracks, features.chapters, features.trickPlay, item.sources, item.tracks, resetAudioTrackState, t, syncCaptionOverlay]);

  const handlePlayerPlay = useCallback(() => {
    setIsPlaying(true);
    const player = playerRef.current as CaptionsCapablePlayer | undefined;
    if (player && !userMutedRef.current) {
      player.setMute?.(false);
      setIsMuted(false);
    }
    applyCaptionsToPlayer(
      player,
      selectedSubtitleRef.current,
      undefined,
      containerRef.current,
    );
    syncCaptionOverlay();
    onPlay?.();
  }, [onPlay, syncCaptionOverlay]);

  const handleFirstFrame = useCallback(() => {
    applyCaptionsToPlayer(
      playerRef.current as CaptionsCapablePlayer | undefined,
      selectedSubtitleRef.current,
      undefined,
      containerRef.current,
    );
    applyAudioTrackToPlayer(
      playerRef.current as CaptionsCapablePlayer | undefined,
      selectedAudioTrackRef.current,
      audioTracksRef.current,
      false,
    );
    void refreshActiveAudioDuration(selectedAudioTrackRef.current);
    syncCaptionOverlay();
    onFirstFrame?.();
  }, [onFirstFrame, syncCaptionOverlay]);

  const handlePlayerPause = useCallback(() => {
    setIsPlaying(false);
    syncCaptionOverlay();
    onPause?.();
  }, [onPause, syncCaptionOverlay]);

  useEffect(() => {
    if (playerProps.autostart || isPlaying) {
      setShowCenterButton(true);
      if (centerButtonHideTimer.current) {
        clearTimeout(centerButtonHideTimer.current);
      }
      centerButtonHideTimer.current = setTimeout(() => {
        setShowCenterButton(false);
      }, PAUSE_PLAY_HIDE_DELAY);
    }

    return () => {
      if (centerButtonHideTimer.current) {
        clearTimeout(centerButtonHideTimer.current);
      }
    };
  }, [playerProps.autostart]);

  const handlePlayPause = () => {
    const player = playerRef.current;
    if (!player) {
      return;
    }

    // Active-a irukkura previous timer-a clear panrom
    if (centerButtonHideTimer.current) {
      clearTimeout(centerButtonHideTimer.current);
    }

    const isCurrentlyPlaying = player.getState() === 'playing';

    if (isCurrentlyPlaying) {
      player.pause();
    } else {
      player.play();
    }

    syncCaptionOverlay();

    // Click pannadhum button-a instantly show panni 1.5s-la hide panrom (Play & Pause rentukume)
    setShowCenterButton(true);
    centerButtonHideTimer.current = setTimeout(() => {
      setShowCenterButton(false);
    }, PAUSE_PLAY_HIDE_DELAY);

    // Reset overlay controls hide timer
    resetHideTimer();
  };

  useEffect(() => {
    return () => {
      if (centerButtonHideTimer.current) {
        clearTimeout(centerButtonHideTimer.current);
      }
    };
  }, []);

  const handleSeek = (newProgress: number) => {
    const player = playerRef.current;
    const totalDuration = player?.getDuration() || duration;
    if (!player || totalDuration <= 0) {
      return;
    }

    player.seek(newProgress * totalDuration);
    const seekTime = newProgress * totalDuration;
    setCurrentTime(seekTime);
    syncCaptionOverlay(seekTime);
    resetHideTimer();
  };

  const handleRewind = () => {
    const player = playerRef.current;
    if (!player) {
      return;
    }

    const seekTime = Math.max(0, player.getPosition() - 10);
    player.seek(seekTime);
    setCurrentTime(seekTime);
    syncCaptionOverlay(seekTime);
    resetHideTimer();
  };

  const handleForward = () => {
    const player = playerRef.current;
    if (!player) {
      return;
    }

    const totalDuration = player.getDuration() || duration;
    const seekTime = Math.min(totalDuration, player.getPosition() + 10);
    player.seek(seekTime);
    setCurrentTime(seekTime);
    syncCaptionOverlay(seekTime);
    resetHideTimer();
  };

  const handleFullscreenToggle = async () => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    if (!document.fullscreenElement) {
      await container.requestFullscreen();
    } else {
      await document.exitFullscreen();
    }
    resetHideTimer();
  };

  const handleContainerClick = (event: React.MouseEvent<HTMLDivElement>) => {
    // Always reset the controls hide timer whenever the user clicks the container
    resetHideTimer();

    const target = event.target as HTMLElement;

    // Ignore clicks on control bars, buttons, headers, or interactive popups
    // to prevent accidental play/pause toggling when using controls
    if (
      target.closest('[data-player-popup]') ||
      target.closest('button') ||
      target.closest(`.${styles.overlay}`) ||
      target.closest(`.${styles.panelDismiss}`)
    ) {
      return;
    }

    // If a settings panel (Quality, Subtitles, etc.) is open, 
    // close it first without toggling video play/pause state
    if (activePanel) {
      setActivePanel(null);
      return;
    }

    // Toggle play/pause when clicking directly on the empty video surface
    handlePlayPause();
  };

  const moreDetailsData = useMemo(() => {
    const durationSeconds = item.duration > 10000 ? Math.floor(item.duration / 1000) : item.duration;
    const durationLabel = durationSeconds ? formatDurationTag(durationSeconds) : '';
    const year = item.pubdate ? new Date(item.pubdate * 1000).getFullYear().toString() : '';
    const primaryMetadata = [year, durationLabel].filter(Boolean).join(' | ');

    return { item, primaryMetadata };
  }, [item]);

  const handleShowMore = () => {
    setActivePanel(null);
    setShowMoreDetails(true);
  };

  const handleCloseMoreDetails = () => {
    setShowMoreDetails(false);
    resetHideTimer();
  };

  const handleBack = () => {
    if (callbacks.onBack) {
      callbacks.onBack();
      return;
    }

    if (onClose) {
      onClose();
      return;
    }

    navigate(-1);
  };

  const handleVolumeToggle = () => {
    const player = playerRef.current;
    if (!player) {
      return;
    }

    const nextMuted = !player.getMute();
    userMutedRef.current = nextMuted;
    audioEndedMuteRef.current = false;
    player.setMute(nextMuted);
    setIsMuted(nextMuted);
    resetHideTimer();
  };

  const handleVolumeChange = (val: number) => {
    const player = playerRef.current;
    if (!player) {
      return;
    }

    player.setVolume(val);
    if (val > 0 && player.getMute()) {
      userMutedRef.current = false;
      audioEndedMuteRef.current = false;
      player.setMute(false);
    }
    if (val === 0) {
      userMutedRef.current = true;
    }
    setVolume(val);
    setIsMuted(val === 0 || player.getMute());
    resetHideTimer();
  };

  const handleQualityChange = (value: string) => {
    const player = playerRef.current as QualityCapablePlayer | undefined;
    if (player) {
      applyQualityToPlayer(player, value);
    } else {
      persistQualityPreference(value, []);
    }

    setSelectedQuality(value);
    setActivePanel(null);
    resetHideTimer();
  };

  const handlePlaybackSpeedChange = (speed: number) => {
    const player = playerRef.current;
    if (player && 'setPlaybackRate' in player) {
      (player as JWPlayer & { setPlaybackRate: (rate: number) => void }).setPlaybackRate(speed);
    }
    setSelectedPlaybackSpeed(speed);
    resetHideTimer();
  };

  const handleSubtitleChange = (value: string) => {
    selectedSubtitleRef.current = value;
    setSelectedSubtitle(value);
    applyCaptionsToPlayer(playerRef.current as CaptionsCapablePlayer | undefined, value, undefined, containerRef.current);
    if (value === 'off') {
      captionTextRef.current = '';
      setCaptionText('');
    } else {
      syncCaptionOverlay();
    }
    resetHideTimer();
  };

  const handleAudioTrackChange = (value: string) => {
    selectedAudioTrackRef.current = value;
    setSelectedAudioTrack(value);
    audioEndedMuteRef.current = false;

    isApplyingAudioTrackRef.current = true;
    applyAudioTrackToPlayer(
      playerRef.current as CaptionsCapablePlayer | undefined,
      value,
      audioTracksRef.current.length ? audioTracksRef.current : undefined,
      true,
    );
    window.setTimeout(() => {
      isApplyingAudioTrackRef.current = false;
    }, 0);

    void refreshActiveAudioDuration(value).then(() => {
      const player = playerRef.current as CaptionsCapablePlayer | undefined;
      if (!player) {
        return;
      }

      applyAudioVideoDurationGuard(player, player.getPosition?.() ?? 0, player.getDuration?.() || duration || 0);

      // Restore audible playback for the newly selected track when user is not muted.
      if (!userMutedRef.current && audioEndedMuteRef.current === false) {
        player.setMute?.(false);
        setIsMuted(false);
      }
    });

    setActivePanel(null);
    resetHideTimer();
  };

  const watermarkPositionClass = {
    'top-left': styles.topLeft,
    'top-right': styles.topRight,
    'bottom-left': styles.bottomLeft,
    'bottom-right': styles.bottomRight,
  }[config?.watermark?.position ?? 'bottom-right'];

  return (
    <div
      ref={containerRef}
      className={classNames(styles.container, {
        [styles.controlsVisible]: showControls || showMoreDetails,
        [styles.captionsRendered]: Boolean(captionText) && selectedSubtitle !== 'off',
        [styles.liveIssueActive]: isLiveIssueOpen,
      })}
      style={themeToCssVars(theme) as React.CSSProperties}
      onMouseMove={resetHideTimer}
      onClick={handleContainerClick}
    >
      <div className={classNames(styles.playerLayer, {
        [styles.isLiveBlur]: showControls || showMoreDetails
      })}>
        <Player
          {...playerProps}
          item={item}
          videoThumnail={videoThumnail}
          onReady={handlePlayerReady}
          onPlay={handlePlayerPlay}
          onPause={handlePlayerPause}
          onFirstFrame={handleFirstFrame}
          onClose={onClose}
          onLiveIssueChange={setIsLiveIssueOpen}
        />
      </div>

      {isLiveIssueOpen && <div className={styles.liveIssueBackdrop} aria-hidden="true" />}

      <div className={styles.gradientTop} />
      <div className={styles.gradientBottom} />

      {captionText && selectedSubtitle !== 'off' && (
        <div className={styles.captionsOverlay} aria-live="polite">
          {captionText}
        </div>
      )}

      {features.watermark && config?.watermark?.enabled && config.watermark.src && (
        <div
          className={classNames(styles.watermark, watermarkPositionClass)}
          style={{ '--watermark-opacity': config.watermark.opacity ?? 0.6 } as React.CSSProperties}
        >
          <img src={config.watermark.src} alt="" />
        </div>
      )}

      {activePanel && (
        <div
          className={styles.panelDismiss}
          onClick={(event) => {
            event.stopPropagation();
            setActivePanel(null);
          }}
        />
      )}

      {!activePanel && !showMoreDetails && !isLiveIssueOpen && showCenterButton && (
        <button
          type="button"
          className={styles.centerPlayButton}
          onClick={(event) => {
            event.stopPropagation(); // Parent container click handler redundant execution prevention
            handlePlayPause();
          }}
          aria-label={isPlaying ? t('pause') : t('play')}
        >
          {playerRef.current?.getState() === 'playing' ? <PauseIcon className={styles.centerPauseIcon} /> : <PlayerPlay />}
        </button>
      )}

      {/* {showSkipIntro && features.skipIntro && (
        <button
          type="button"
          className={skipIntroStyles.skipIntro}
          onClick={() => {
            setActivePanel(null);
            setShowSkipIntro(false);
            callbacks.onSkipIntro?.();
          }}
        >
            {t('skip_intro')}
        </button>
      )} */}

      <div className={classNames(styles.overlay, { [styles.hidden]: !showControls && !showMoreDetails && !isLiveIssueOpen })}>
        <CustomPlayerHeader
          item={item}
          title={item.title}
          description={item.description}
          likeCount={likeCount}
          isLiked={isLiked}
          showReport={features.report}
          showLike={features.like}
          showDislike={features.dislike}
          showShare={features.share}
          onBack={handleBack}
          onReport={callbacks.onReport}
          onLike={() => {
            setIsLiked((prev) => !prev);
            setLikeCount((prev) => (isLiked ? prev - 1 : prev + 1));
            callbacks.onLike?.();
          }}
          onDislike={callbacks.onDislike}
          onShare={callbacks.onShare}
          onMoreClick={handleShowMore}
        />

        <div className={styles.bottomControls} hidden={isLiveIssueOpen}>
          <CustomPlayerProgress
            currentTime={currentTime}
            duration={duration}
            progress={progress}
            buffered={0.6}
            isScrubbing={isScrubbing}
            scrubProgress={scrubProgress}
            chapters={chapters}
            showTrickPlay={(!isLiveContent && features.trickPlay)}
            trickPlayFrames={trickPlayTrack.frames}
            trickPlayKind={trickPlayTrack.kind}
            trickPlayFallback={posterSrc}
            onSeek={handleSeek}
            onScrubStart={() => {
              isScrubbingRef.current = true;
              setIsScrubbing(true);
            }}
            onScrubEnd={() => {
              isScrubbingRef.current = false;
              setIsScrubbing(false);
              syncCaptionOverlay();
            }}
            onScrub={(progress) => {
              setScrubProgress(progress);
              const totalDuration = playerRef.current?.getDuration() || duration;
              if (totalDuration > 0) {
                syncCaptionOverlay(progress * totalDuration);
              }
            }}
          />

          <CustomPlayerControls
            isPlaying={isPlaying}
            isMuted={isMuted}
            volume={volume}
            isFullscreen={isFullscreen}
            activePanel={activePanel}
            qualityOptions={qualityOptions}
            subtitleOptions={subtitleOptions}
            audioTrackOptions={audioTrackOptions}
            playbackSpeedOptions={playbackSpeedOptions}
            selectedQuality={selectedQuality}
            selectedSubtitle={selectedSubtitle}
            selectedAudioTrack={selectedAudioTrack}
            selectedPlaybackSpeed={selectedPlaybackSpeed}
            showQuality={features.quality}
            showSubtitles={showSubtitlesControl}
            showAudioTracks={showAudioTracksControl}
            showPlaybackSpeed={features.playbackSpeed}
            showPip={features.pip}
            showUpNext={features.upNext}
            onPlayPause={handlePlayPause}
            onRewind={handleRewind}
            onForward={handleForward}
            onVolumeToggle={handleVolumeToggle}
            onVolumeChange={handleVolumeChange}
            onPanelToggle={setActivePanel}
            onQualityChange={handleQualityChange}
            onSubtitleChange={handleSubtitleChange}
            onAudioTrackChange={handleAudioTrackChange}
            onPlaybackSpeedChange={handlePlaybackSpeedChange}
            onFullscreenToggle={handleFullscreenToggle}
            onMoreVideos={callbacks.onMoreVideos}
          />
        </div>
      </div>

      {showMoreDetails && (
        <div
          className={styles.moreDetailsOverlay}
          data-player-popup
          onClick={(event) => {
            event.stopPropagation();
            if (event.target === event.currentTarget) {
              handleCloseMoreDetails();
            }
          }}
        >
          <div
            className={styles.moreDetailsDialog}
            role="dialog"
            aria-modal="true"
            aria-label={item.title}
            style={universalStyles.modalBackgroundColors}
            onClick={(event) => event.stopPropagation()}
          >
            <ModalCloseButton onClick={handleCloseMoreDetails} />
            <ShowMoreDetails data={moreDetailsData} />
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomPlayer;
